import { useReducer } from 'react';

import { match } from '@utils/match';
import { randomId } from '@utils/strings';

import { FilterComparer, FilterConfig, FilterData, FilterValue } from './common';
import { multiSelectComparers, resolveAutoComparer } from './filter-utils';

import { FilterActions } from '../table-store';

enum CurrentFilterActionType {
  SelectCategory,
  SelectComparer,
  SelectField,
  SelectValue,
  UnselectCategory,
  UnselectComparer,
  UnselectField,
}

export const useCurrentFilter = ({
  closeMenu,
  initialData,
  config,
  collectionActions: { addFilter, updateFilter, removeFilter },
}: {
  closeMenu(): void;
  initialData?: Partial<FilterData>;
  config: FilterConfig;
  collectionActions: FilterActions;
}) => {
  const [state, dispatch] = useReducer(
    currentFilterStateReducer,
    initialData,
    (seed): CurrentFilterState => {
      if (!seed) return {};
      if (seed.comparer) return seed as CurrentFilterState;

      const autoComparer = resolveAutoComparer(config, seed.category, seed.field);
      if (!autoComparer) return seed as CurrentFilterState;

      return {
        ...seed,
        comparer: autoComparer,
        value: multiSelectComparers.includes(autoComparer) ? [] : '',
      };
    }
  );

  const actions: CurrentFilterActions = {
    selectCategory: (data) => dispatch({ type: CurrentFilterActionType.SelectCategory, data }),
    selectComparer: (data) => dispatch({ type: CurrentFilterActionType.SelectComparer, data }),
    selectValue: (data) => dispatch({ type: CurrentFilterActionType.SelectValue, data }),
    selectField: (data) => dispatch({ type: CurrentFilterActionType.SelectField, data }),
    unselectField: () => dispatch({ type: CurrentFilterActionType.UnselectField }),
    unselectCategory: () => dispatch({ type: CurrentFilterActionType.UnselectCategory }),
    unselectComparer: () => dispatch({ type: CurrentFilterActionType.UnselectComparer }),
    applyFilter: (data) => {
      const category = state.category || data?.category;
      if (!category) return;

      const method = state.id === undefined ? addFilter : updateFilter;
      method({
        id: state.id || randomId(),
        category: category,
        comparer: data?.comparer ?? (state.comparer as FilterComparer),
        field: data?.field ?? state.field,
        value: (data?.value ?? state.value) as FilterValue,
      });

      closeMenu();
    },
    removeFilter: () => {
      if (state.id !== undefined) {
        removeFilter(state.id);
      }
    },
  };

  return [state, actions] as const;
};

const currentFilterReducers: {
  [P in CurrentFilterActionType]: (
    state: CurrentFilterState,
    action: Extract<CurrentFilterAction, { type: P }>
  ) => CurrentFilterState;
} = {
  [CurrentFilterActionType.SelectComparer]: (state, action) => {
    return {
      ...state,
      comparer: action.data,
      value: multiSelectComparers.includes(action.data) ? [] : '',
    };
  },
  [CurrentFilterActionType.UnselectField]: (state) => {
    return {
      ...state,
      field: undefined,
      comparer: undefined,
      value: undefined,
    };
  },
  [CurrentFilterActionType.SelectField]: (state, action) => {
    return {
      ...state,
      field: action.data,
    };
  },
  [CurrentFilterActionType.SelectValue]: (state, action) => {
    const currentValue = state.value;

    if (Array.isArray(currentValue)) {
      if (Array.isArray(action.data)) {
        return {
          ...state,
          value: action.data,
        };
      }

      return {
        ...state,
        value: currentValue.includes(action.data)
          ? currentValue.filter((val) => val !== action.data)
          : [...currentValue, action.data],
      };
    }

    return {
      ...state,
      value: action.data,
    };
  },
  [CurrentFilterActionType.UnselectComparer]: (state) => {
    return {
      ...state,
      comparer: undefined,
      value: undefined,
    };
  },
  [CurrentFilterActionType.SelectCategory]: (state, action) => {
    return {
      ...state,
      category: action.data,
    };
  },
  [CurrentFilterActionType.UnselectCategory]: (state) => {
    return {
      ...state,
      category: undefined,
      comparer: undefined,
      field: undefined,
      value: undefined,
    };
  },
};

const currentFilterStateReducer = (state: CurrentFilterState, action: CurrentFilterAction) => {
  return match(action.type, currentFilterReducers, state, action);
};

export type CurrentFilterActions = {
  selectComparer(comparer: FilterComparer): void;
  unselectComparer(): void;

  selectCategory(category: string): void;
  unselectCategory(): void;

  selectField(value: string): void;
  unselectField(): void;

  selectValue(value: string | string[]): void;
  applyFilter(overrideData?: Partial<FilterData>): void;
  removeFilter(): void;
};

export type CurrentFilterState = {
  id?: string;
  category?: string;
  comparer?: FilterComparer;
  value?: FilterValue;
  field?: string;
};

type CurrentFilterAction =
  | { type: CurrentFilterActionType.SelectCategory; data: string }
  | { type: CurrentFilterActionType.SelectComparer; data: FilterComparer }
  | { type: CurrentFilterActionType.SelectField; data: string }
  | { type: CurrentFilterActionType.SelectValue; data: string | string[] }
  | { type: CurrentFilterActionType.UnselectCategory }
  | { type: CurrentFilterActionType.UnselectComparer }
  | { type: CurrentFilterActionType.UnselectField };
